import { create } from 'zustand';
import axiosInstance from '../lib/axios';

export const useNotificationStore = create((set, get) => ({
    notifications: [],
    loading: false,
    unreadCount: 0,

    fetchNotifications: async () => {
        try {
            set({ loading: true });
            const response = await axiosInstance.get('/api/notifications');
            const notifications = Array.isArray(response.data) ? response.data : [];
            set({ 
                notifications, 
                unreadCount: notifications.length,
                loading: false 
            });
        } catch (error) {
            console.error('Error fetching notifications:', error);
            set({ loading: false });
        }
    },

    markAsRead: async (id) => {
        try {
            await axiosInstance.post(`/api/notifications/${id}/read`);
            set((state) => {
                const updated = state.notifications.filter(n => n.id !== id);
                return {
                    notifications: updated,
                    unreadCount: updated.length
                };
            });
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    },

    markAllRead: async () => {
        try {
            await axiosInstance.post('/api/notifications/mark-all-read');
            set({ notifications: [], unreadCount: 0 });
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    },

    clearConversationNotifications: (conversationId) => {
        set((state) => {
            const updated = state.notifications.filter(n => String(n.data.conversation_id) !== String(conversationId));
            return {
                notifications: updated,
                unreadCount: updated.length
            };
        });
    },

    // Start listening for notifications
    startListening: (userId) => {
        if (!userId) return;
        
        const channel = `App.Models.User.${userId}`;
        console.log(`Subscribing to channel: ${channel}`);
        
        import('../lib/echo').then((module) => {
            const echo = module.default;
            const privateChannel = echo.private(channel);

            // Listen for DB notifications
            privateChannel.notification((notification) => {
                console.log('Real-time notification received:', notification);
                
                // Laravel sometimes nests the data depending on the driver
                const data = notification.data || notification;
                
                const VISIBLE_TYPES = ['human_escalation', 'lead_assigned', 'new_lead', 'form_lead', 'email', 'campaign', 'system_alert'];
                if (!VISIBLE_TYPES.includes(data.type)) {
                    console.log('Ignoring non-relevant notification type:', data.type);
                    return;
                }

                const normalizedNotification = {
                    id: notification.id || Math.random().toString(36).substr(2, 9),
                    created_at: new Date().toISOString(),
                    data: {
                        type: data.type,
                        subject: data.subject,
                        from: data.from,
                        snippet: data.snippet,
                        contact_id: data.contact_id,
                        email_message_id: data.email_message_id,
                        conversation_id: data.conversation_id,
                        message_id: data.message_id,
                        action_url: data.action_url,
                    }
                };

                set((state) => ({
                    notifications: [normalizedNotification, ...state.notifications],
                    unreadCount: state.unreadCount + 1
                }));
            });

            // Listen for conversation read sync across tabs
            privateChannel.listen('.App\\Events\\ConversationRead', (event) => {
                console.log('Conversation read sync signal received:', event);
                get().clearConversationNotifications(event.conversationId);
            });
        });

        return () => {
            import('../lib/echo').then((module) => {
                const echo = module.default;
                echo.leave(channel);
            });
        };
    },

    // Start polling fallback (10s) to ensure sync if WebSockets fail
    startPolling: () => {
        get().fetchNotifications();
        const interval = setInterval(() => {
            get().fetchNotifications();
        }, 10000);
        return () => clearInterval(interval);
    }
}));
